import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { getApiUrl } from "@/utils/api";
import { useBasket } from "@/context/BasketContext";
import { useShopperAuth } from "@/context/ShopperAuthContext";
import ShopperAuthCard from "@/components/ShopperAuthCard";
import CheckoutHeader from "@/components/CheckoutHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = getApiUrl();

/**
 * Embeddable Checkout module — the same customer form + Stripe hand-off as
 * /checkout, but rendered inline so admins can slot it into a "One-page shop"
 * flow (e.g. Products module + Basket module + Checkout module on a single
 * modular page).
 *
 * Config:
 *   - title         : section heading (default "Checkout")
 *   - subtitle      : optional blurb
 *   - empty_url     : where to send shoppers if their basket is empty
 *   - empty_label   : label of that link
 */
export default function CheckoutModule({ config = {} }) {
  const {
    title = "Checkout",
    subtitle = "You'll be redirected to Stripe's secure checkout to complete payment.",
    empty_url = "/",
    empty_label = "Continue shopping",
  } = config;
  const basket = useBasket();
  const { shopper, updateProfile } = useShopperAuth();
  const [customer, setCustomer] = useState({
    name: "", email: "", phone: "",
    address: { line1: "", line2: "", city: "", postcode: "", country: "" },
  });
  const [products, setProducts] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoError, setPromoError] = useState("");

  // Pre-fill from the signed-in shopper — but only when the user hasn't
  // already started typing, so we don't clobber in-progress edits.
  useEffect(() => {
    if (!shopper) return;
    const savedAddr = shopper.billing_address || {};
    setCustomer((prev) => ({
      name: prev.name || shopper.name || "",
      email: prev.email || shopper.email || "",
      phone: prev.phone || shopper.phone || "",
      address: {
        line1: prev.address.line1 || savedAddr.line1 || "",
        line2: prev.address.line2 || savedAddr.line2 || "",
        city: prev.address.city || savedAddr.city || "",
        postcode: prev.address.postcode || savedAddr.postcode || "",
        country: prev.address.country || savedAddr.country || "",
      },
    }));
  }, [shopper]);

  useEffect(() => {
    axios.get(`${API}/products`).then(({ data }) => {
      const byId = {};
      (data.products || []).forEach((p) => { byId[p.product_id] = p; });
      setProducts(byId);
    }).catch(() => {});
  }, []);

  const lines = basket.items.map((i) => ({ ...i, product: products[i.product_id] })).filter((l) => l.product);
  const subtotal = lines.reduce((s, l) => s + Number(l.product.price || 0), 0);
  const currency = lines[0]?.product?.currency || "GBP";
  const discount = promoApplied ? Number(promoApplied.discount || 0) : 0;
  const total = Math.max(0, subtotal - discount);

  const applyPromo = async () => {
    if (!promoCode.trim()) { setPromoError("Enter a code"); return; }
    setPromoBusy(true); setPromoError("");
    try {
      const { data } = await axios.post(`${API}/discount/validate`, {
        code: promoCode.trim(), subtotal,
      });
      setPromoApplied(data);
      toast.success(`${data.code} applied — you save ${currency} ${Number(data.discount).toFixed(2)}`);
    } catch (e) {
      setPromoApplied(null);
      setPromoError(e.response?.data?.detail || "Code not accepted");
    } finally { setPromoBusy(false); }
  };
  const removePromo = () => { setPromoApplied(null); setPromoCode(""); setPromoError(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!customer.name.trim() || !customer.email.trim()) {
      toast.error("Name and email are required"); return;
    }
    if (basket.count === 0) { toast.error("Basket is empty"); return; }
    try {
      setSubmitting(true);
      // Await the profile save so it lands before the Stripe redirect fires
      // and cancels any in-flight requests. Non-critical on failure.
      if (shopper) {
        try {
          await updateProfile({
            name: customer.name,
            phone: customer.phone,
            billing_address: customer.address,
          });
        } catch { /* non-critical — don't block the checkout */ }
      }
      const contextPropertyId = (() => {
        try { return sessionStorage.getItem("context_property_id") || null; } catch { return null; }
      })();
      const { data } = await axios.post(`${API}/checkout/session`, {
        items: basket.items,
        origin_url: window.location.origin,
        customer,
        promo_code: promoApplied ? promoApplied.code : null,
        context_property_id: contextPropertyId,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
      setSubmitting(false);
    }
  };

  return (
    <section className="py-12 px-6" data-testid="checkout-module">
      <div className="max-w-4xl mx-auto">
        <CheckoutHeader title={title} />
        {subtitle && <p className="text-sm text-muted-foreground mb-6 -mt-2 max-w-2xl">{subtitle}</p>}

        {basket.count === 0 ? (
          <div className="border border-dashed rounded p-8 text-center">
            <p className="text-muted-foreground mb-3">Your basket is empty.</p>
            <Link to={empty_url} className="text-primary underline text-sm">{empty_label}</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            <form onSubmit={submit} className="md:col-span-2 space-y-4 bg-background border rounded p-5">
              <ShopperAuthCard />
              <h3 className="font-semibold">Your details</h3>
              <div>
                <Label>Full name *</Label>
                <Input value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} required data-testid="checkout-module-name" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} required data-testid="checkout-module-email" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} data-testid="checkout-module-phone" />
              </div>
              <div className="pt-2 border-t">
                <h3 className="font-semibold mb-2">Billing address</h3>
                {shopper && (
                  <p className="text-xs text-muted-foreground mb-3" data-testid="checkout-module-address-remember">
                    We&apos;ll remember this on <strong>{shopper.email}</strong> for next time.
                  </p>
                )}
                <div className="grid gap-3">
                  <div>
                    <Label>Address line 1</Label>
                    <Input value={customer.address.line1} onChange={(e) => setCustomer({ ...customer, address: { ...customer.address, line1: e.target.value } })} data-testid="checkout-module-addr-line1" />
                  </div>
                  <div>
                    <Label>Address line 2 <span className="text-xs text-muted-foreground">(optional)</span></Label>
                    <Input value={customer.address.line2} onChange={(e) => setCustomer({ ...customer, address: { ...customer.address, line2: e.target.value } })} data-testid="checkout-module-addr-line2" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>City / Town</Label>
                      <Input value={customer.address.city} onChange={(e) => setCustomer({ ...customer, address: { ...customer.address, city: e.target.value } })} data-testid="checkout-module-addr-city" />
                    </div>
                    <div>
                      <Label>Postcode</Label>
                      <Input value={customer.address.postcode} onChange={(e) => setCustomer({ ...customer, address: { ...customer.address, postcode: e.target.value.toUpperCase() } })} data-testid="checkout-module-addr-postcode" />
                    </div>
                  </div>
                  <div>
                    <Label>Country</Label>
                    <Input value={customer.address.country} onChange={(e) => setCustomer({ ...customer, address: { ...customer.address, country: e.target.value } })} placeholder="United Kingdom" data-testid="checkout-module-addr-country" />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
                <Lock className="h-3 w-3" />
                Payments handled securely by Stripe.
              </div>
              <Button type="submit" disabled={submitting} className="w-full" data-testid="checkout-module-submit">
                {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Redirecting…</> : `Pay ${currency} ${total.toFixed(2)}`}
              </Button>
            </form>

            <aside className="border rounded p-4 h-fit bg-background space-y-3">
              <h3 className="font-semibold">Order summary</h3>
              {lines.map((l) => (
                <div key={l.product_id} className="flex justify-between text-sm">
                  <span className="truncate pr-2">{l.product.name}</span>
                  <span className="whitespace-nowrap">{currency} {Number(l.product.price || 0).toFixed(2)}</span>
                </div>
              ))}

              <div className="border-t pt-3 space-y-2">
                {promoApplied ? (
                  <div className="flex items-center justify-between text-sm bg-green-50 border border-green-200 rounded px-3 py-2">
                    <div>
                      <div className="font-mono font-semibold text-green-800">{promoApplied.code}</div>
                      <div className="text-xs text-green-700">
                        {promoApplied.kind === "percent" ? `${promoApplied.value}% off` : `${currency} ${Number(promoApplied.value).toFixed(2)} off`}
                      </div>
                    </div>
                    <button type="button" onClick={removePromo} className="text-xs text-red-600 hover:underline">Remove</button>
                  </div>
                ) : (
                  <>
                    <Label className="text-xs">Have a discount code?</Label>
                    <div className="flex gap-2">
                      <Input value={promoCode} onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(""); }} placeholder="Enter code" className="font-mono uppercase h-9" data-testid="checkout-module-promo-input" />
                      <Button type="button" size="sm" variant="outline" onClick={applyPromo} disabled={promoBusy} data-testid="checkout-module-promo-apply">
                        {promoBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Apply"}
                      </Button>
                    </div>
                    {promoError && <p className="text-xs text-red-600">{promoError}</p>}
                  </>
                )}
              </div>

              <div className="border-t pt-3 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{currency} {subtotal.toFixed(2)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-green-700">
                    <span>Discount</span>
                    <span>−{currency} {discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-serif text-lg pt-1">
                  <span>Total</span>
                  <span>{currency} {total.toFixed(2)}</span>
                </div>
              </div>
            </aside>
          </div>
        )}
      </div>
    </section>
  );
}
